import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/i18n/LanguageProvider';
import { supabase } from '@/integrations/supabase/client';
import { fitnessWorkoutController } from '@/lib/fitnessWorkoutController';
import { reviewWorkoutDiscard, discardReviewedWorkout, type WorkoutDiscardReview } from '@/lib/fitness/workoutDiscard';
import { notifyFitnessChanged } from '@/hooks/useFitnessResource';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from '@/components/ui/alert-dialog';

export default function WorkoutCancelButton({ ownerId, onDiscard, className = '' }: {
  ownerId: string; onDiscard: () => void; className?: string;
}) {
  const { t } = useLanguage();
  const [review, setReview] = useState<WorkoutDiscardReview | null>(null);
  const [error, setError] = useState(false), [busy, setBusy] = useState(false);
  const lock = useRef(false), lifecycle = useRef(0), mounted = useRef(false);
  useEffect(() => {
    const generation = lifecycle; mounted.current = true; generation.current++; setReview(null); setError(false); setBusy(false); lock.current = false;
    return () => { mounted.current = false; generation.current++; };
  }, [ownerId]);
  const open = () => {
    if (lock.current) return;
    setError(false);
    try {
      const run = fitnessWorkoutController.getOwned(ownerId);
      if (run?.state.exercises.length && !fitnessWorkoutController.pauseForClose(ownerId)) throw new Error('busy');
      setReview(reviewWorkoutDiscard(ownerId));
    } catch { setError(true); }
  };
  const confirm = async () => {
    if (!review || lock.current) return;
    const original = review, generation = lifecycle.current;
    const current = () => mounted.current && lifecycle.current === generation;
    lock.current = true; setBusy(true); setError(false);
    try {
      const auth = await supabase.auth.getUser();
      if (!current()) return;
      if (auth.error || auth.data.user?.id !== original.ownerId || original.ownerId !== ownerId) throw new Error('changed');
      await discardReviewedWorkout(original);
      if (!current()) return;
      setReview(null); notifyFitnessChanged(); onDiscard();
    } catch { if (current()) setError(true); }
    finally { if (current()) { lock.current = false; setBusy(false); } }
  };
  return <>
    <button type="button" onClick={open} className={'min-h-11 text-sm text-destructive underline underline-offset-4 ' + className}>
      {t('fitness.builder.cancelAndNew')}
    </button>
    {error && !review && <p role="alert" className="text-sm text-destructive">{t('fitness.builder.workoutChanged')}</p>}
    <AlertDialog open={!!review && review.ownerId === ownerId} onOpenChange={value => { if (!value && !lock.current) setReview(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('fitness.builder.cancelTitle')}</AlertDialogTitle>
          <AlertDialogDescription>{t('fitness.builder.cancelDescription')}</AlertDialogDescription>
        </AlertDialogHeader>
        {error && <p role="alert" className="text-sm text-destructive">{t('fitness.builder.workoutChanged')}</p>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t('common.back')}</AlertDialogCancel>
          <button type="button" disabled={busy} onClick={() => void confirm()}
            className="min-h-11 rounded-xl bg-destructive px-4 py-3 text-sm font-semibold text-destructive-foreground disabled:opacity-50">
            {t('fitness.builder.cancelAndNew')}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
}
